'use client';

import { useTranslations } from 'next-intl';
import { SwiperContainer } from '@/components/containers/swiper-container';
import { OddsButton } from '@/components/widgets/odds-button';
import { useAccountInfo } from '@/hooks/query/account';
import { getSportSetting } from '@/services/settings.service';
import { betTypeService } from '@/services/bettype.service';
import { matchService } from '@/services/match.service';
import { DisplayLineInfo, getDisplayLineInfo, getSortedESportMarkets, getSortedMarkets } from '@/utils/odds-utils';
import { SportMatchModel, Market } from '@nf/types/odds';
import { MatchEventWrapper } from './match-event-wrapper';
import { MatchTeams } from '../match-teams';
import { MatchInfo } from '../../match-info';
import { ScoreItem, scoreboardService } from '@/services/scoreboard.service';
import { Box, IO, MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import { TranslateType } from '@/types/bet-type-settings';
import { useEffect, useMemo, useState } from 'react';
import { itemsEmptyCheck, stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { useGetBetTypeSetting } from '@/hooks';
import { MatchOddsLoadingWrapper } from '../../match-odds-loading-wrapper';
import { MatchOption } from './match-option';
import { ArrowRightIcon } from '@/components/icons/svg-icons';

interface CardContentProps {
	match: SportMatchModel;
	markets: Market[];
	isLoading: boolean;
	homeTeamName: string;
	awayTeamName: string;
	sport: number;
}
export const CardSportMatch = ({
	match,
	markets,
	isLoading,
	homeTeamName,
	awayTeamName,
	sport,
}: CardContentProps) => {
	const { user } = useAccountInfo();
	const t = useTranslations('General');
	const betTypeTranslate = useTranslations('BetType');
	const [arrangedMarkets, setArrangedMarkes] = useState<Market[]>([]);
	const [isArranging, setIsArranging] = useState<boolean>(true);
	const teamScoreList = useMemo(() => scoreboardService.getAllSportScore(match), [match]);
	const [homeScoreList, awayScoreList] = useMemo(() => [
		scoreboardService.getMatchCricketScore(match, 'home'),
		scoreboardService.getMatchCricketScore(match, 'away')
	], [match])
	const strongTeam = matchService.getStrongTeam(match.BetTypes);
	const getBetTypeSetting = useGetBetTypeSetting();
	const sportSetting = getSportSetting(sport);
	const betTypesSet: Set<number> = sportSetting.oddsPageFilterBetTypes.reduce((accumulator, currentValue) => {
		currentValue.betTypes.forEach(betType => accumulator.add(betType))
		return accumulator;
	}, new Set() as Set<number>);

	const getFirstScoreText = (scoreList: ScoreItem[]): string => itemsEmptyCheck(scoreList)
		.chain((scoreList: ScoreItem[]) => stringEmptyCheck(scoreList[0]?.text))
		.fold(
			() => '',
			(teamScore: string) => teamScore
		)

	const [teamAScore, teamBScore] = matchEquals(sport)
		.on(50, () => [
			getFirstScoreText(homeScoreList),
			getFirstScoreText(awayScoreList)]
		)
		.on(44, () => [
			`${t('lbl_round')}${getFirstScoreText(teamScoreList)}`,
			'']
		)
		.otherwise(() => getFirstScoreText(teamScoreList).split('-'))

	const getTranslatedLine = ({ prefixLabel, displayLine }: DisplayLineInfo) => stringEmptyCheck(prefixLabel)
		.fold(
			() => displayLine,
			(label: string) => `${t(label)}${displayLine}`
		)

	useEffect(() => {
		MaybeMatch(!isLoading)
			.chain(() => itemsEmptyCheck(markets))
			.fold(
				() => [],
				() => IO.of(markets)
					.map((markets: Market[]) => sport === 43 ? getSortedESportMarkets(betTypesSet, match.IsLive, markets) : getSortedMarkets(betTypesSet, markets))
					.map(setArrangedMarkes)
					.map(() => isArranging && setIsArranging(false))
					.run()
			)
	}, [isLoading, markets])

	const slideElements = arrangedMarkets.map(market => {
		const betTypeTitle = Box(betTypeService.getBetTypeSettingTitle(market?.BetTypeId, match?.GameID ?? 0, market.Resourceid))
			.map(([label, translateType]: [string, TranslateType]) => matchEquals(translateType)
				.on('General', () => t.raw(label))
				.on('BetType', () => betTypeTranslate(label))
				.otherwise(() => '')
			)
			.map((translatedTitle: string) => betTypeService.getBetTypeReplaceTitle(
				translatedTitle,
				match?.GameID ?? 0,
				market
			))
			.extract();
		const betTypeSetting = getBetTypeSetting(market.BetTypeId)
		return (
			<>
				<div className="match__bettype-title">{betTypeTitle}</div>
				<div className="match__odds-button-group">
					{
						betTypeSetting?.betChoices.map(betChoice => {
							const goal = Box.of({
								line: market.Line,
								betType: market.BetTypeId,
								betTeam: betChoice.betTeam,
							})
								.map(getDisplayLineInfo)
								.map(getTranslatedLine)
								.fold((lineText: string) => stringEmptyCheck(lineText)
									.fold(
										() => t(betTypeService.getBetChoiceDisplaySettingLabel(market.BetTypeId, betChoice.betTeam)),
										(line: string) => line
									))
							const displayClassName = betTypeService.getBetChoiceDisplaySettingBetTypeClassName(market.BetTypeId, betChoice.betTeam);
							return (
								<OddsButton 
									key={`${market.MatchId}_${market.MarketId}_${market.BetTypeId}_${betChoice.betTeam}`}
									goal={goal}
									match={match}
									market={market}
									selection={market.Selections[betChoice.betTeam]}
									homeTeamName={homeTeamName}
									awayTeamName={awayTeamName}
									variant={displayClassName} 
									betTeam={market.Selections[betChoice.betTeam].SelId} 
									originalPrice={market.Selections[betChoice.betTeam].Price}
									sport={sport}
									type="OU"
									oddsType={user?.OType}
									accountStatus={user?.ActStatus ?? 0}
								/>
							)
						})
					}
				</div>
			</>
		);
	})
	return (
		<div className="match">
			<MatchEventWrapper
				sport={sport}
				market={match.MaT}
				leagueId={match.LeagueId}
				matchId={match.MatchId}
			>
				<div className="match-info-container">
					<MatchInfo 
						match={match}
						betTypeCount={match.Moc + markets.length}
						oddsPageLayout="card"
					/>
				</div>
				<MatchTeams
					scoreA={teamAScore}
					scoreB={teamBScore}
					isLive={match.IsLive}
					homeRedCardCount={match.Rc1}
					awayRedCardCount={match.Rc2}
					teamIdA={match.TeamId1}
					teamIdB={match.TeamId2}
					teamNameA={homeTeamName}
					teamNameB={awayTeamName}
					sportId={sport}
					serveTeam={scoreboardService.getServeTeam(match, sport)}
					strongTeam={strongTeam}
				/>
				<div className="match-option-container">
					<MatchOption match={match} oddsPageLayout={'card'} />
					<div className="btn-more-match">
						<span>{`${match.Moc + markets.length >= 100 ? '99+' : match.Moc + markets.length}`}</span>
						<ArrowRightIcon />
					</div>
				</div>
			</MatchEventWrapper>
			<div className="match__odds">
				<MatchOddsLoadingWrapper isLoading={isLoading || isArranging}>
					<SwiperContainer
						slideElements={slideElements}
						slideElementsBetTypeIds={arrangedMarkets.map(market => market.BetTypeId)}
						className="match__bettypes" 
						elementClassName="match__bettype"
					/>
				</MatchOddsLoadingWrapper>
			</div>
		</div>
	)
}


